import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MspAccountsHeader from "../../../SubscriptionBackendPortal/mspAccountsSubscription/msp-accounts-page-header/MspAccountsHeader";
import { MspAccountsTable } from "../../../SubscriptionBackendPortal/mspAccountsSubscription/msp-accounts-table";
import PaginationButtonsBlock from "../../../pagination-block/pagination-buttons-block";
import useFilters from "../../../../hooks/filters.hook";
import { useEntity } from "../../../../hooks/use-entity.hook";
import { Flaggers } from "../../../../common/store/flaggers/flaggers.enum";
import { FlaggersMethods } from "../../../../common/store/flaggers/flaggers.methods";
import InviteMspModal from "../../../SubscriptionBackendPortal/mspAccountsSubscription/invite-msp-modal/invite-msp.modal";
import useDateFormat from "../../../../hooks/use-date-format.hook";
import { ROUTE_PATHS } from "../../../../constants/app.constants";
import styles from "./distributor-msp-accounts.module.scss";

const DistributorMspAccounts = () => {
  const { distributorId } = useParams();
  const navigate = useNavigate();
  const variant = "v2";
  const isDistributor = false;
  const isDistributorView = true;
  const isArchived = false;

  const [selectedRows, setSelectedRows] = useState([]);
  const [forceShowCheckboxes, setForceShowCheckboxes] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [listDataLoading] = useState(false);
  const [totalClients] = useState(0);
  const [headersList, setHeadersList] = useState([]);
  const [bannerData] = useState({});
  const SearchInput = useRef();
  const [searchString, setSearchString] = useState("");
  const [, setSearchInput] = useState(false);
  const dateFormat = useDateFormat();

  const { setEntityData: setMspInviteData } = useEntity({
    entityType: Flaggers.MspInviteFlagger,
  });

  const { selectedFilters, setSelectedFilters, addFilter } = useFilters({
    onFiltersUpdate: (filters) => {
      // Don't call API for v2 variant
      if (variant === "v2") {
        return;
      }
    },
  });

  const handleSelectionChange = (selected) => {
    setSelectedRows(selected);
    if (selected.length >= 2 && !forceShowCheckboxes) {
      setForceShowCheckboxes(true);
    }
  };

  const handleRowClick = (mspAccountId) => {
    if (isDistributorView && mspAccountId) {
      navigate(ROUTE_PATHS.subscriptionPortalPages.distributorDetailsPages.mspAccountOverview(distributorId, mspAccountId));
    }
  };

  const handleBulkAction = (action) => {
    console.log(`Bulk action: ${action}`, selectedRows);
    if (action === "close") {
      setForceShowCheckboxes(false);
      setSelectedRows([]);
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRows = () => {
    setForceShowCheckboxes(true);
  };

  const handleInputChange = (event) => {
    const { value } = event.target;
    setSearchString(value);
  };

  const sortData = {
    sortItem: "Company_Name",
    sortOrder: 1,
    sorting: false,
  };

  const sortRecords = (_fieldKey) => {
    console.log("Sort records:", _fieldKey);
  };

  const getPageData = async () => {
    // Placeholder for future API calls
    return Promise.resolve();
  };

  const tableFilters = useMemo(() => {
    if (variant === "v2") {
      const filters = [];

      // Add "Distributor" filter if isDistributor is false
      if (isDistributor === false) {
        filters.push({
          filterType: "radio-range",
          filterKey: "distributor",
          filterTitle: "Distributor",
          text: "Distributor",
        });
      }

      // Add the rest of the filters
      filters.push(
        {
          filterType: "radio-range",
          filterKey: "salesRep",
          filterTitle: "Sales Rep",
          text: "Sales Rep",
        },
        {
          filterType: "radio-range",
          filterKey: 6,
          filterTitle: "Subscription",
          filterValue: bannerData?.plans?.map((item) => ({
            value: false,
            label: item?.Label,
            key: item?.Label,
          })),
          text: "Subscription",
        },
        {
          filterType: "radio-range",
          filterKey: "overSubscription",
          filterTitle: "Over-Subscription",
          text: "Over-Subscription",
        },
        {
          filterType: "radio-range",
          filterKey: "renewal",
          filterTitle: "Renewal",
          text: "Renewal",
        },
        {
          filterType: "radio-range",
          filterKey: "tags",
          filterTitle: "Tags",
          text: "Tags",
        },
        {
          filterType: "radio-range",
          filterKey: "autoRenewal",
          filterTitle: "Auto Renewal",
          text: "Auto Renewal",
        },
        {
          filterType: "radio-range",
          filterKey: "status",
          filterTitle: "Status",
          text: "Status",
        }
      );

      return filters;
    }

    return [];
  }, [variant, bannerData?.plans, isDistributor]);

  return (
    <div className={styles.container}>
      <MspAccountsHeader
        variant={variant}
        totalClients={totalClients}
        listDataLoading={listDataLoading}
        searchString={searchString}
        searchInputRef={SearchInput}
        handleInputChange={handleInputChange}
        setSearchInput={setSearchInput}
        tableFilters={tableFilters}
        addFilter={addFilter}
        headersList={headersList}
        setHeadersList={setHeadersList}
        getPageData={getPageData}
        sortData={sortData}
        sortRecords={sortRecords}
        onInviteClick={() => {
          setMspInviteData(
            {
              showModal: true,
              companyName: "",
              userName: "",
              companyEmail: "",
              country: "",
            },
            FlaggersMethods.UpdateFlagger
          );
        }}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        dateFormat={dateFormat}
        isArchived={isArchived}
        selectedRows={selectedRows}
        onBulkAction={handleBulkAction}
        onSelectRows={handleSelectRows}
        forceShowCheckboxes={forceShowCheckboxes}
        isDistributorView={isDistributorView}
      />

      <div className={styles.tableContainer}>
        <MspAccountsTable
          isArchived={isArchived}
          onSelectionChange={handleSelectionChange}
          forceShowCheckboxes={forceShowCheckboxes}
          variant={variant}
          isDistributor={isDistributor}
          isDistributorView={isDistributorView}
          onRowClick={handleRowClick}
        />
      </div>

      <div className={styles.paginationContainer}>
        <PaginationButtonsBlock
          pageNumber={currentPage}
          maxPageNumber={10}
          onButtonClick={(page) => setCurrentPage(page)}
          onArrowClick={(isNext) => {
            if (isNext === false) {
              setCurrentPage((prev) => Math.max(prev - 1, 1));
            } else {
              setCurrentPage((prev) => Math.min(prev + 1, 10));
            }
          }}
        />
      </div>

      <InviteMspModal />
    </div>
  );
};

export default DistributorMspAccounts;
